import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useWholesale } from '../contexts/WholesaleContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

// Password Change Form Component
const PasswordChangeForm = ({ token }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validate passwords
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }

    setLoading(true);
    try {
      await api.changeWholesalePassword(token, currentPassword, newPassword);
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to change password' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-alt-bg p-8">
      <h2 className="font-display text-2xl mb-6">Change Password</h2>
      
      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        {message.text && (
          <div className={`p-4 text-sm ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {message.text}
          </div>
        )}

        <div>
          <Label htmlFor="currentPassword" className="text-sm font-semibold uppercase tracking-wider mb-2">
            Current Password
          </Label>
          <Input
            id="currentPassword"
            type="password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full"
          />
        </div>

        <div>
          <Label htmlFor="newPassword" className="text-sm font-semibold uppercase tracking-wider mb-2">
            New Password
          </Label>
          <Input
            id="newPassword"
            type="password"
            required
            minLength={6}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full"
          />
        </div>

        <div>
          <Label htmlFor="confirmPassword" className="text-sm font-semibold uppercase tracking-wider mb-2">
            Confirm New Password
          </Label>
          <Input
            id="confirmPassword"
            type="password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full"
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="bg-cape-red hover:bg-red-800 text-white"
        >
          {loading ? 'Changing...' : 'Change Password'}
        </Button>
      </form>
    </div>
  );
};

const WholesaleDashboardPage = () => {
  const navigate = useNavigate();
  const { client, token, isAuthenticated, loading: authLoading, logout } = useWholesale();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState({});
  const [activeTab, setActiveTab] = useState('products');
  const [loading, setLoading] = useState(true);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/wholesale/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch products and orders
  useEffect(() => {
    const fetchData = async () => {
      if (token) {
        try {
          const [productsData, ordersData] = await Promise.all([
            api.getWholesaleProducts(token),
            api.getWholesaleOrders(token),
          ]);
          setProducts(productsData);
          setOrders(ordersData.orders || []);
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [token]);

  const handleQuantityChange = (sku, quantity) => {
    const qty = parseInt(quantity) || 0;
    if (qty > 0) {
      setCart({ ...cart, [sku]: qty });
    } else {
      const newCart = { ...cart };
      delete newCart[sku];
      setCart(newCart);
    }
  };

  const getCartTotal = () => {
    return Object.entries(cart).reduce((total, [sku, qty]) => {
      const product = products.find(p => p.sku === sku);
      return total + (product ? product.wholesaleCost * qty : 0);
    }, 0);
  };

  const getCartItemCount = () => {
    return Object.values(cart).reduce((sum, qty) => sum + qty, 0);
  };

  const handleSubmitOrder = async () => {
    if (getCartItemCount() === 0) return;

    setOrderLoading(true);
    try {
      const items = Object.entries(cart).map(([sku, quantity]) => {
        const product = products.find(p => p.sku === sku);
        return {
          sku,
          name: product.name,
          wholesale_price: product.wholesaleCost,
          quantity,
        };
      });

      await api.createWholesaleOrder(token, { items, notes: orderNotes });
      setOrderSuccess(true);
      setCart({});
      setOrderNotes('');
      
      // Refresh orders
      const ordersData = await api.getWholesaleOrders(token);
      setOrders(ordersData.orders || []);
      
      setTimeout(() => setOrderSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting order:', error);
      alert('Failed to submit order. Please try again.');
    } finally {
      setOrderLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="paper-texture min-h-screen py-12 flex items-center justify-center">
        <p className="text-[#8C8885]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="paper-texture min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-display text-4xl mb-2">Wholesale Portal</h1>
            <p className="text-[#8C8885]">Welcome, {client?.company_name}</p>
          </div>
          <div className="flex gap-4">
            <Link to="/">
              <Button variant="outline">View Retail Store</Button>
            </Link>
            <Button onClick={logout} variant="outline" className="text-red-600 border-red-600">
              Logout
            </Button>
          </div>
        </div>

        {/* Success Message */}
        {orderSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700">
            Order submitted successfully! We will contact you to confirm your order.
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-alt-bg">
          <button
            onClick={() => setActiveTab('products')}
            className={`pb-4 px-4 font-semibold ${activeTab === 'products' ? 'border-b-2 border-cape-red text-cape-red' : 'text-[#8C8885]'}`}
          >
            Products & Order
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`pb-4 px-4 font-semibold ${activeTab === 'orders' ? 'border-b-2 border-cape-red text-cape-red' : 'text-[#8C8885]'}`}
          >
            Order History ({orders.length})
          </button>
          <button
            onClick={() => setActiveTab('account')}
            className={`pb-4 px-4 font-semibold ${activeTab === 'account' ? 'border-b-2 border-cape-red text-cape-red' : 'text-[#8C8885]'}`}
          >
            Account Info
          </button>
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Products List */}
            <div className="lg:col-span-3">
              <div className="bg-white border border-alt-bg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-alt-bg">
                    <tr>
                      <th className="text-left p-4 font-semibold text-sm uppercase">Product</th>
                      <th className="text-center p-4 font-semibold text-sm uppercase">Wholesale</th>
                      <th className="text-center p-4 font-semibold text-sm uppercase">Retail</th>
                      <th className="text-center p-4 font-semibold text-sm uppercase w-32">Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.sku} className="border-t border-alt-bg hover:bg-gray-50">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            {product.imageUrl && (
                              <img
                                src={product.imageUrl}
                                alt={product.name}
                                className="w-12 h-12 object-cover rounded"
                              />
                            )}
                            <div>
                              <p className="font-semibold">{product.name}</p>
                              <p className="text-xs text-[#8C8885]">{product.sku}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-center font-bold text-cape-red">
                          ${product.wholesaleCost.toFixed(2)}
                        </td>
                        <td className="p-4 text-center text-[#8C8885]">
                          ${product.retailPrice.toFixed(2)}
                        </td>
                        <td className="p-4 text-center">
                          <Input
                            type="number"
                            min="0"
                            value={cart[product.sku] || ''}
                            onChange={(e) => handleQuantityChange(product.sku, e.target.value)}
                            className="w-20 text-center mx-auto"
                            placeholder="0"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-alt-bg p-6 sticky top-24">
                <h2 className="font-display text-xl mb-4">Order Summary</h2>
                
                {getCartItemCount() === 0 ? (
                  <p className="text-[#8C8885] text-sm">Add products to your order</p>
                ) : (
                  <>
                    <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                      {Object.entries(cart).map(([sku, qty]) => {
                        const product = products.find(p => p.sku === sku);
                        if (!product) return null;
                        return (
                          <div key={sku} className="flex justify-between text-sm">
                            <span>{product.name} × {qty}</span>
                            <span className="font-semibold">${(product.wholesaleCost * qty).toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="border-t border-alt-bg pt-4 mb-4">
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total ({getCartItemCount()} items)</span>
                        <span className="text-cape-red">${getCartTotal().toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="text-sm font-semibold uppercase tracking-wider mb-2 block">
                        Order Notes
                      </label>
                      <textarea
                        value={orderNotes}
                        onChange={(e) => setOrderNotes(e.target.value)}
                        className="w-full p-2 border border-input rounded text-sm"
                        rows={2}
                        placeholder="Special instructions..."
                      />
                    </div>

                    <Button
                      onClick={handleSubmitOrder}
                      disabled={orderLoading}
                      className="w-full bg-cape-red hover:bg-red-800 text-white"
                    >
                      {orderLoading ? 'Submitting...' : 'Submit Order'}
                    </Button>
                    
                    <p className="text-xs text-[#8C8885] mt-2 text-center">
                      We will contact you to confirm payment & shipping
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="bg-white border border-alt-bg">
            {orders.length === 0 ? (
              <div className="p-8 text-center text-[#8C8885]">
                No orders yet. Start by placing your first wholesale order!
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-alt-bg">
                  <tr>
                    <th className="text-left p-4 font-semibold text-sm uppercase">Order ID</th>
                    <th className="text-left p-4 font-semibold text-sm uppercase">Date</th>
                    <th className="text-center p-4 font-semibold text-sm uppercase">Items</th>
                    <th className="text-center p-4 font-semibold text-sm uppercase">Total</th>
                    <th className="text-center p-4 font-semibold text-sm uppercase">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} className="border-t border-alt-bg">
                      <td className="p-4 font-mono text-sm">{order.id.slice(0, 8)}...</td>
                      <td className="p-4">{new Date(order.created_at).toLocaleDateString()}</td>
                      <td className="p-4 text-center">
                        {order.items.reduce((sum, item) => sum + item.quantity, 0)} jars
                      </td>
                      <td className="p-4 text-center font-bold">${order.subtotal.toFixed(2)}</td>
                      <td className="p-4 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          order.status === 'completed' ? 'bg-green-100 text-green-700' :
                          order.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Account Tab */}
        {activeTab === 'account' && client && (
          <div className="max-w-2xl space-y-6">
            <div className="bg-white border border-alt-bg p-8">
              <h2 className="font-display text-2xl mb-6">Account Information</h2>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-[#8C8885] uppercase mb-1">Company Name</p>
                  <p className="font-semibold">{client.company_name}</p>
                </div>
                <div>
                  <p className="text-sm text-[#8C8885] uppercase mb-1">Contact Name</p>
                  <p className="font-semibold">{client.contact_name}</p>
                </div>
                <div>
                  <p className="text-sm text-[#8C8885] uppercase mb-1">Email</p>
                  <p className="font-semibold">{client.email}</p>
                </div>
                <div>
                  <p className="text-sm text-[#8C8885] uppercase mb-1">Phone</p>
                  <p className="font-semibold">{client.phone}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-[#8C8885] uppercase mb-1">Address</p>
                  <p className="font-semibold">
                    {client.address}, {client.city}, {client.state} {client.zip_code}
                  </p>
                </div>
                {client.tax_id && (
                  <div>
                    <p className="text-sm text-[#8C8885] uppercase mb-1">Tax ID</p>
                    <p className="font-semibold">{client.tax_id}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-[#8C8885] uppercase mb-1">Account Status</p>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                    Approved
                  </span>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-alt-bg">
                <p className="text-sm text-[#8C8885]">
                  Need to update your information? Contact us at{' '}
                  <a href="mailto:contactus@preservesofcapecod.com" className="text-cape-red hover:underline">
                    contactus@preservesofcapecod.com
                  </a>
                </p>
              </div>
            </div>

            {/* Password Change Section */}
            <PasswordChangeForm token={token} />
          </div>
        )}
      </div>
    </div>
  );
};

export default WholesaleDashboardPage;
